import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, HostListener } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Observable, of, forkJoin, map } from 'rxjs';
import { switchMap, finalize, catchError, tap, withLatestFrom, shareReplay, take } from 'rxjs/operators';
import { HotelService } from '../../services/hotel.service';
import { SearchService } from '../../services/search.service';
import { toLocalISOString } from '../../core/utils/date.util';
import { BookingService, ValidateBookingRequest } from '../../services/booking.service';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { InstallmentBadgeComponent } from '../../shared/components/installment-badge/installment-badge.component';

@Component({
  selector: 'app-hotel-detail',
  standalone: true,
  imports: [CommonModule, LoadingComponent, InstallmentBadgeComponent],
  templateUrl: './hotel-detail.component.html',
  styleUrls: ['./hotel-detail.component.css']
})
export class HotelDetailComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly hotelService = inject(HotelService);
  private readonly searchService = inject(SearchService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly bookingService = inject(BookingService);
  private readonly cdr = inject(ChangeDetectorRef);

  hotelWithPeriods$: Observable<{ details: any, periods: any[], firstPrice: number | null, mapUrl: SafeResourceUrl | null, availability: any[], parsedAvailability: any[], roomGroups: any[], pricingDetails: any }> | null = null;

  constructor() {
  }
  isMapModalOpen = false;
  isGalleryModalOpen = false;
  currentImageIndex: number = 0;
  private galleryAutoSlideTimer: any = null;

  startGalleryAutoSlide(images: any[]): void {
    this.stopGalleryAutoSlide();
    if (!images || images.length < 2) return;
    this.galleryAutoSlideTimer = setInterval(() => {
      this.currentImageIndex = (this.currentImageIndex + 1) % images.length;
      this.cdr.markForCheck();
    }, 4000);
  }

  stopGalleryAutoSlide(): void {
    if (this.galleryAutoSlideTimer) {
      clearInterval(this.galleryAutoSlideTimer);
      this.galleryAutoSlideTimer = null;
    }
  }

  // Touch swipe
  private touchStartX = 0;
  private touchCurrentImages: any[] = [];

  onGalleryTouchStart(e: TouchEvent, images: any[]): void {
    this.touchStartX = e.touches[0].clientX;
    this.touchCurrentImages = images;
    this.stopGalleryAutoSlide();
  }

  onGalleryTouchEnd(e: TouchEvent): void {
    const diff = this.touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40 && this.touchCurrentImages.length > 1) {
      if (diff > 0) {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.touchCurrentImages.length;
      } else {
        this.currentImageIndex = (this.currentImageIndex - 1 + this.touchCurrentImages.length) % this.touchCurrentImages.length;
      }
    }
    this.startGalleryAutoSlide(this.touchCurrentImages);
  }

  // Room inspect swipe
  onRoomInspectTouchStart(e: TouchEvent): void {
    this.touchStartX = e.touches[0].clientX;
  }

  onMRoomTouchStart(e: TouchEvent, room: any): void {
    this.touchStartX = e.touches[0].clientX;
  }

  onMRoomTouchEnd(e: TouchEvent, room: any): void {
    if (!room.roomImages || room.roomImages.length < 2) return;
    const diff = this.touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      const idx = room._mobileImgIdx || 0;
      if (diff > 0) {
        room._mobileImgIdx = (idx + 1) % room.roomImages.length;
      } else {
        room._mobileImgIdx = (idx - 1 + room.roomImages.length) % room.roomImages.length;
      }
    }
  }

  onRoomInspectTouchEnd(e: TouchEvent): void {
    if (!this.inspectRoom?.roomImages?.length || this.inspectRoom.roomImages.length < 2) return;
    const diff = this.touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        this.inspectRoomImgIdx = (this.inspectRoomImgIdx + 1) % this.inspectRoom.roomImages.length;
      } else {
        this.inspectRoomImgIdx = (this.inspectRoomImgIdx - 1 + this.inspectRoom.roomImages.length) % this.inspectRoom.roomImages.length;
      }
    }
  }

  ngOnDestroy(): void {
    this.stopGalleryAutoSlide();
    document.body.style.overflow = '';
  }
  mapLoaded = false;
  
  // Oda galeri modal
  isRoomGalleryModalOpen = false;
  currentRoomImages: any[] = [];
  currentRoomImageIndex: number = 0;
  showAllFacilities = false;
  showAvailabilityDetails: { [roomKey: string]: boolean } = {};
  showAllRoomFacilities: { [roomKey: string]: boolean } = {};
  facilitiesSheet: { title: string; groups: { name: string; items: any[] }[] } | null = null;

  inspectRoom: any = null;
  inspectRoomImgIdx = 0;
  isMobileView = false;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobileView = window.innerWidth < 768;
  }

  isFullscreenCalendarOpen = false;

  openDatePicker(): void {
    if (this.isMobileView) {
      this.isFullscreenCalendarOpen = true;
      this.isMinNightCalendarOpen = true;
      if (!this.inlineCalendarMonth) this.inlineCalendarMonth = new Date();
      this.generateInlineCalendarDays();
      document.body.style.overflow = 'hidden';
    } else {
      this.scrollToDateSelection();
      if (!this.isMinNightCalendarOpen) {
        this.toggleMinNightCalendar();
      }
    }
  }

  closeFullscreenCalendar(): void {
    this.isFullscreenCalendarOpen = false;
    document.body.style.overflow = '';
  }

  confirmCalendarDates(): void {
    this.isFullscreenCalendarOpen = false;
    document.body.style.overflow = '';
    if (this.minNightCheckInDate && this.minNightCheckOutDate) {
      this.searchWithCurrentDates();
    }
  }

  selectCalendarDate(day: any): void {
    if (day.isPast) return;
    if (!this.inlineCalendarSelectionStart || (this.inlineCalendarSelectionStart && this.inlineCalendarSelectionEnd)) {
      this.inlineCalendarSelectionStart = day.date;
      this.inlineCalendarSelectionEnd = null;
      this.minNightCheckInDate = day.date;
      this.minNightCheckOutDate = null;
    } else {
      if (day.date > this.inlineCalendarSelectionStart) {
        this.inlineCalendarSelectionEnd = day.date;
        this.minNightCheckOutDate = day.date;
      } else {
        this.inlineCalendarSelectionStart = day.date;
        this.minNightCheckInDate = day.date;
      }
    }
    this.generateInlineCalendarDays();
  }

  openRoomInspect(room: any): void {
    this.inspectRoom = room;
    this.inspectRoomImgIdx = 0;
    document.body.style.overflow = 'hidden';
  }

  onModalClose(): void {
    document.body.style.overflow = '';
  }
  activeFacilityGroup = 0;

  konumModal: {
    mapUrl: any;
    nearDistances: any[];
    hotelName?: string;
    hotelImage?: string;
    rating?: number | null;
  } | null = null;

  openKonumModal(mapUrl: any, nearDistances: any[], hotelData?: any): void {
    this.konumModal = {
      mapUrl,
      nearDistances: nearDistances || [],
      hotelName: hotelData?.name || this.hotelPreview?.name,
      hotelImage: hotelData?.image || hotelData?.images?.[0]?.url || this.hotelPreview?.image,
      rating: hotelData?.rating ?? this.hotelPreview?.rating ?? null
    };
    document.body.style.overflow = 'hidden';
  }

  closeKonumModal(): void {
    this.konumModal = null;
    document.body.style.overflow = '';
  }

  goBack(): void {
    if (window.history.length > 1) window.history.back();
    else this.router.navigate(['/']);
  }

  getNightCount(): number {
    if (!this.minNightCheckInDate || !this.minNightCheckOutDate) return 0;
    const ms = new Date(this.minNightCheckOutDate).getTime() - new Date(this.minNightCheckInDate).getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }

  onShareHotel(hotel: any): void {
    const url = window.location.href;
    const title = hotel?.name || 'Pusula Tatil';
    const nav: any = navigator;
    if (nav.share) {
      nav.share({ title, text: title, url }).catch(() => {});
    } else if (nav.clipboard?.writeText) {
      nav.clipboard.writeText(url);
    }
  }

  private stripHtml(s: any): string {
    if (typeof s !== 'string') return '';
    return s.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }

  getHotelAddress(h: any): string {
    if (!h) return this.stripHtml(this.hotelPreview?.location);
    const parts = [
      h.address,
      h.neighbourhood?.name || h.neighbourhood,
      h.town?.name || h.town,
      h.city?.name || h.city,
      h.country?.name || h.country
    ]
      .map((x: any) => this.stripHtml(x))
      .filter((x: string) => !!x);

    if (parts.length > 0) return parts.join(', ');
    return this.stripHtml(this.hotelPreview?.location);
  }

  private categoryNameMap: Record<number, string> = {
    0: 'Genel',
    1: 'Konsept',
    2: 'Otel Olanakları',
    3: 'Yeme & İçme',
    4: 'Aktiviteler',
    5: 'Spor & Eğlence',
    6: 'Hizmetler'
  };

  openHotelFacilitiesSheet(items: any[]): void {
    const list = items || [];
    const map = new Map<number, { name: string; items: any[] }>();
    for (const f of list) {
      const id = (f.facilityTypeId ?? 0) as number;
      const name = f.facilityTypeName || this.categoryNameMap[id] || 'Diğer';
      if (!map.has(id)) map.set(id, { name, items: [] });
      map.get(id)!.items.push(f);
    }
    const groups = Array.from(map.values()).sort((a, b) => b.items.length - a.items.length);
    if (groups.length === 1) groups[0].name = 'Tümü';

    this.facilitiesSheet = { title: 'Otel Özellikleri', groups };
    this.activeFacilityGroup = 0;
    document.body.style.overflow = 'hidden';
  }

  setFacilityGroup(idx: number): void {
    this.activeFacilityGroup = idx;
    setTimeout(() => {
      const tabs = document.querySelectorAll<HTMLElement>('.fac-sheet__tab');
      const container = document.querySelector<HTMLElement>('.fac-sheet__tabs');
      if (!container) return;
      const next = tabs[idx + 1] || tabs[idx];
      if (!next) return;
      const cRect = container.getBoundingClientRect();
      const nRect = next.getBoundingClientRect();
      const delta = nRect.right - cRect.right + 16;
      if (delta > 0) container.scrollBy({ left: delta, behavior: 'smooth' });
      else if (nRect.left < cRect.left) {
        container.scrollBy({ left: nRect.left - cRect.left - 16, behavior: 'smooth' });
      }
    }, 0);
  }

  getFacilitiesTotal(): number {
    if (!this.facilitiesSheet) return 0;
    return this.facilitiesSheet.groups.reduce((sum, g) => sum + g.items.length, 0);
  }

  closeFacilitiesSheet(): void {
    this.facilitiesSheet = null;
    document.body.style.overflow = '';
  }
  selectedAddons: { [addonKey: string]: boolean } = {};
  currentTotalPrice = 0;
  searchCheckInDate: string = '';
  searchCheckOutDate: string = '';
  adultCount: number = 2;
  childCount: number = 0;
  roomCount: number = 1;
  childrenAges: number[] = []; // Çocuk yaşları
  
  // Tarihsiz arama flag'i
  isDatelessSearch = false;
  maxInstallment: number = 9;

  // Anında gösterim için önizleme verisi (search'ten geliyor)
  hotelPreview: any = null;

  // Çoklu oda seçimi
  selectedRoomByPosition: { [position: number]: string } = {};
  matchedGroupIndex: number = -1;
  showAllRoomsForPosition: { [position: number]: boolean } = {};
  multiRoomInitialCount = 3;

  // Misafir seçici
  isGuestSelectorOpen = false;

  // Mobilde search bar toggle
  isMobileSearchExpanded = false;
  isSearchPopoverOpen = false;
  
  // Minimum gece takvimi
  isMinNightCalendarOpen = false;
  isSearching = false;
  minNightCheckInDate: Date | null = null;
  minNightCheckOutDate: Date | null = null;
  
  // Inline takvim için
  inlineCalendarMonth: Date = new Date();
  inlineCalendarDays: any[] = [];
  inlineCalendarSelectionStart: Date | null = null;
  inlineCalendarSelectionEnd: Date | null = null;
  currentHotelId: string = '';
  
  // Rezervasyon doğrulama durumu
  bookingValidationState: { [roomKey: string]: { loading: boolean; error: string | null } } = {};
  
  // Search parametrelerini sakla
  currentSearchParams: any = null;
  
  // Addon işlemleri için gerekli
  originalProductKey: string = ''; // URL'den gelen orijinal key
  productKey: string = ''; // RoomGroup.Key (GetDetail'den gelen) veya AddOn sonrası güncel key
  roomGroupKey: string = ''; // İlk GetDetail'den gelen RoomGroup.Key (items[0].key)
  traceId: string = ''; // ⭐ GetDetail response'ından gelen TraceId (AddOn için kullanılacak)
  passengerKeys: string[] = [];
  isAddonProcessing: { [key: string]: boolean } = {};
  errorMessage: string | null = null;
  
  // Minimum gece kısıtlaması
  hasMinNightRestriction = false;
  minNightCount: number = 0;
  minNightMessage: string = '';
  alternativeRoomGroups: any[] = [];

  /**
   * Sayfa dışına tıklandığında dropdown'ları kapat
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Event target kontrolü
    const target = event.target as HTMLElement;
    
    // Misafir seçici dışına tıklandıysa kapat
    if (this.isGuestSelectorOpen && !target.closest('.guest-selector-container')) {
      this.isGuestSelectorOpen = false;
    }
    
    // Takvim dışına tıklandıysa kapat
    if (this.isMinNightCalendarOpen && !target.closest('.calendar-container')) {
      this.isMinNightCalendarOpen = false;
    }
  }

  ngOnInit(): void {
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' as any }), 0);
    this.isMobileView = window.innerWidth < 768;
    this.maxInstallment = this.searchService.getMaxInstallmentCount();
    this.hotelPreview = this.searchService.getSelectedHotelPreview();

    // Original ProductKey'i URL'den al
    this.route.paramMap.subscribe(params => {
      const encodedProductKey = params.get('productKey');
      if (encodedProductKey) {
        this.originalProductKey = decodeURIComponent(encodedProductKey);
        this.productKey = this.originalProductKey; // İlk değer olarak set et
      }
    });
    
    // ⚠️ PassengerKeys'i SearchService'den al (FALLBACK - GetDetail'de güncellenecek)
    this.passengerKeys = this.searchService.getPassengerKeys();
    
    // ⭐ KRİTİK: SearchService'ten minNight kısıtlaması bilgisini al (ÖNCELİKLİ)
    const minNightInfo = this.searchService.getMinNightRestrictionInfo();
    if (minNightInfo.hasRestriction) {
      this.hasMinNightRestriction = true;
      this.minNightCount = minNightInfo.minNight;
      this.minNightMessage = minNightInfo.message;
      this.alternativeRoomGroups = this.searchService.getAlternativeRoomGroupData() || [];
      
    }
    
    // ⚠️ SADECE route.paramMap değiştiğinde tetiklen (searchResults$ değiştiğinde DEĞİL)
    // searchResults'ı withLatestFrom ile al (re-trigger olmaz)
    this.hotelWithPeriods$ = this.route.paramMap.pipe(
      withLatestFrom(this.searchService.searchResults$),
      switchMap(([params, searchResults]) => {
        const encodedProductKey = params.get('productKey');
        
        
        // ⭐ KRİTİK: Her route değişiminde minNight kısıtlamasını yeniden kontrol et
        const minNightInfo = this.searchService.getMinNightRestrictionInfo();
        this.hasMinNightRestriction = minNightInfo.hasRestriction;
        this.minNightCount = minNightInfo.minNight;
        this.minNightMessage = minNightInfo.message;
        this.alternativeRoomGroups = minNightInfo.hasRestriction 
          ? (this.searchService.getAlternativeRoomGroupData() || []) 
          : [];
        

        if (encodedProductKey) {
          const productKey = decodeURIComponent(encodedProductKey);
          
          return this.hotelService.getHotelDetails(productKey).pipe(
            map(details => ({ details, periods: null as any })),
            tap((response) => {
              // TraceId'yi GetDetail response'ından al
              if (response.details?.traceId) {
                this.traceId = response.details.traceId;
              } else {
                const cookieTraceId = this.hotelService.getTraceIdFromCookie();
                if (cookieTraceId) {
                  this.traceId = cookieTraceId;
                }
              }
            }),
            switchMap(response => {
              // ⭐ Önce rooms hashmap resolve et (availability key extraction'dan ÖNCE!)
              if (response.details?.items && Array.isArray(response.details.items)) {
                const tlr = response.details.rooms;
                const isHash = tlr && typeof tlr === 'object' && !Array.isArray(tlr);
                response.details.items = response.details.items.map((rg: any) => {
                  if (rg.rooms && Array.isArray(rg.rooms) && rg.rooms.length > 0) return rg;
                  if (isHash && Array.isArray(rg.roomKeys) && rg.roomKeys.length > 0) {
                    const resolved = rg.roomKeys.map((k: any) => tlr[k]).filter(Boolean);
                    if (resolved.length > 0) return { ...rg, rooms: resolved };
                  }
                  if (rg.roomDetails && typeof rg.roomDetails === 'object' && !Array.isArray(rg.roomDetails)) {
                    const resolved = Object.values(rg.roomDetails);
                    if (resolved.length > 0) return { ...rg, rooms: resolved };
                  }
                  if (Array.isArray(tlr) && tlr.length > 0) return { ...rg, rooms: tlr };
                  return rg;
                });
              }

              // ⭐ Resolve edilmiş rooms'tan passengerKeys çek (search'te yoksa)
              if (!this.passengerKeys || this.passengerKeys.length === 0) {
                if (response.details?.items && Array.isArray(response.details.items)) {
                  for (const item of response.details.items) {
                    // items[].passengerKeys
                    if (item.passengerKeys?.length) {
                      this.passengerKeys = [...item.passengerKeys];
                      this.searchService.updatePassengerKeys(this.passengerKeys);
                      break;
                    }
                    // items[].rooms[].passengerKeys
                    if (item.rooms && Array.isArray(item.rooms)) {
                      for (const room of item.rooms) {
                        if (room.passengerKeys?.length) {
                          this.passengerKeys = [...room.passengerKeys];
                          this.searchService.updatePassengerKeys(this.passengerKeys);
                          break;
                        }
                      }
                    }
                    if (this.passengerKeys.length > 0) break;
                  }
                }
              }

              // Availability çağrılarını LAZY yap - sayfa render'ını beklemesin
              const processedRoomKeys = new Set<string>();
              const availabilityRequests: { productKey: string, roomKey: string }[] = [];

              if (searchResults) {
                const keys = this.hotelService.extractKeysFromSearchResponse(searchResults);
                const matchingKeys = keys.filter(k => k.productKey === productKey);
                matchingKeys.forEach(key => {
                  if (key.roomKey && !processedRoomKeys.has(key.roomKey)) {
                    processedRoomKeys.add(key.roomKey);
                    availabilityRequests.push({ productKey: key.productKey, roomKey: key.roomKey });
                  }
                });
              }

              if (response.details?.items && Array.isArray(response.details.items)) {
                response.details.items.forEach((roomGroup: any) => {
                  if (roomGroup.rooms && Array.isArray(roomGroup.rooms)) {
                    roomGroup.rooms.forEach((room: any) => {
                      const roomKey = room.key || room.roomKey;
                      if (roomKey && !processedRoomKeys.has(roomKey)) {
                        processedRoomKeys.add(roomKey);
                        availabilityRequests.push({ productKey, roomKey });
                      }
                    });
                  }
                });
              }

              // Availability'yi arka planda yükle, sayfayı bloklamadan
              this.loadAvailabilityLazy(availabilityRequests, searchResults);

              return of({
                hotelData: response,
                availabilityData: [] as any[]
              });
            }),
            map(({ hotelData, availabilityData }) => {
              let mapUrl: SafeResourceUrl | null = null;

              if (hotelData.details?.hotel?.coordinate) {
                const { latitude, longitude } = hotelData.details.hotel.coordinate;
                if (latitude && longitude) {
                  const url = `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
                  mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                }
              }
              
              let parsedAvailability: any[] = [];
              if (availabilityData && availabilityData.length > 0) {
                let checkInDate: Date | undefined;
                let checkOutDate: Date | undefined;
                
                if (searchResults && searchResults.searchCriteria) {
                  const checkIn = searchResults.searchCriteria.checkInDate;
                  const checkOut = searchResults.searchCriteria.checkOutDate;
                  
                  if (checkIn) {
                    checkInDate = new Date(checkIn);
                  }
                  if (checkOut) {
                    checkOutDate = new Date(checkOut);
                  }
                  
                  // hotelId'yi searchCriteria.destinations'dan al
                  const destinations = (searchResults.searchCriteria as any).destinations;
                  if (!this.currentHotelId && destinations && destinations.length > 0) {
                    const dest = destinations[0];
                    if (dest.hotelId) {
                      this.currentHotelId = dest.hotelId;
                    } else if (dest.destinationId && dest.locationType === 2) {
                      // locationType 2 = Hotel
                      this.currentHotelId = dest.destinationId;
                    }
                  }
                }
                
                parsedAvailability = this.hotelService.parseAvailabilityData(
                  availabilityData, 
                  checkInDate, 
                  checkOutDate
                );
              }
              
              let roomGroups: any[] = [];
              let pricingDetails: any = null;
              
              // ⭐ KRİTİK: RoomGroup.Key'i al (items[0].key) - AddOn işlemlerinde kullanılacak
              if (hotelData.details && hotelData.details.items && Array.isArray(hotelData.details.items) && hotelData.details.items.length > 0) {
                this.roomGroupKey = hotelData.details.items[0].key;
                // AddOn işlemleri için productKey'i RoomGroup.Key yap
                this.productKey = this.roomGroupKey;
              } else {
              }
              
              // ⭐ hotelId'yi API response'undan al (fallback)
              if (!this.currentHotelId && hotelData.details?.hotelId) {
                this.currentHotelId = hotelData.details.hotelId;
              }

              // Galeri otomatik kaydırma başlat
              if (hotelData.details?.hotel?.images?.length) {
                this.currentImageIndex = 0;
                this.startGalleryAutoSlide(hotelData.details.hotel.images);
              }
              if (!this.currentHotelId && hotelData.details?.items?.[0]?.hotelId) {
                this.currentHotelId = hotelData.details.items[0].hotelId;
              }
              
              // Extract pricing details from search results
              if (searchResults) {
                pricingDetails = this.hotelService.extractPricingDetails(searchResults, productKey);
                
                // Extract search dates and hotelId from first item
                if (searchResults.items && searchResults.items.length > 0) {
                  const firstItem = searchResults.items[0];
                  this.searchCheckInDate = firstItem.checkInDate;
                  this.searchCheckOutDate = firstItem.checkOutDate;
                  // Hotel ID'yi al
                  if (firstItem.hotelId) {
                    this.currentHotelId = firstItem.hotelId;
                  }
                }
                
                // Fallback: userSearchCriteria'dan tarihleri ve hotelId al
                const userCriteria = this.searchService.getUserSearchCriteria();
                
                // hotelId'yi userCriteria'dan al (any cast ile)
                if (!this.currentHotelId && (userCriteria as any)?.hotelId) {
                  this.currentHotelId = (userCriteria as any).hotelId;
                }
                if (!this.currentHotelId && (userCriteria as any)?.destinationId) {
                  this.currentHotelId = (userCriteria as any).destinationId;
                }
                
                if (!this.searchCheckInDate || !this.searchCheckOutDate) {
                  if (userCriteria) {
                    // checkInDate Date veya string olabilir
                    if (userCriteria.checkInDate) {
                      this.searchCheckInDate = typeof userCriteria.checkInDate === 'string'
                        ? userCriteria.checkInDate
                        : toLocalISOString(userCriteria.checkInDate as Date);
                    }
                    if (userCriteria.checkOutDate) {
                      this.searchCheckOutDate = typeof userCriteria.checkOutDate === 'string'
                        ? userCriteria.checkOutDate
                        : toLocalISOString(userCriteria.checkOutDate as Date);
                    }
                  }
                }
                
                // minNightCheckInDate ve minNightCheckOutDate'i set et (takvim için)
                // userCriteria'dan direkt al (daha güvenilir)
                if (userCriteria?.checkInDate) {
                  this.minNightCheckInDate = new Date(userCriteria.checkInDate);
                } else if (this.searchCheckInDate) {
                  this.minNightCheckInDate = new Date(this.searchCheckInDate);
                }
                
                if (userCriteria?.checkOutDate) {
                  this.minNightCheckOutDate = new Date(userCriteria.checkOutDate);
                } else if (this.searchCheckOutDate) {
                  this.minNightCheckOutDate = new Date(this.searchCheckOutDate);
                }
                

                // Tarihsiz arama kontrolü - userCriteria'dan kontrol et (API kendi tarih koyabiliyor)
                if (userCriteria && !userCriteria.checkInDate && !userCriteria.checkOutDate) {
                  this.isDatelessSearch = true;
                } else {
                  const hasValidCheckIn = this.searchCheckInDate && !isNaN(new Date(this.searchCheckInDate).getTime());
                  const hasValidCheckOut = this.searchCheckOutDate && !isNaN(new Date(this.searchCheckOutDate).getTime());
                  if (!hasValidCheckIn || !hasValidCheckOut) {
                    this.isDatelessSearch = true;
                  }
                }

                // Search parametrelerini sakla
                this.currentSearchParams = searchResults.searchCriteria;
                
                // Yetişkin ve çocuk sayısını hesapla - userSearchCriteria öncelikli
                if (userCriteria) {
                  this.adultCount = userCriteria.adults || 2;
                  this.childCount = userCriteria.children || 0;
                  this.roomCount = userCriteria.rooms || 1;
                  this.childrenAges = userCriteria.childrenAges ? [...userCriteria.childrenAges] : [];
                } else if (searchResults.searchCriteria?.passengers && Array.isArray(searchResults.searchCriteria.passengers) && searchResults.searchCriteria.passengers.length > 0) {
                  this.adultCount = searchResults.searchCriteria.passengers.filter((p: any) => p.passengerType === 0).length;
                  this.childCount = searchResults.searchCriteria.passengers.filter((p: any) => p.passengerType === 1).length;
                  this.roomCount = searchResults.searchCriteria.rooms?.length || 1;
                  this.childrenAges = searchResults.searchCriteria.passengers
                    .filter((p: any) => p.passengerType === 1)
                    .map((p: any) => p.age || 5);
                } else {
                  this.adultCount = 2;
                  this.childCount = 0;
                  this.roomCount = 1;
                  this.childrenAges = [];
                }
                
                if (this.adultCount === 0) {
                  this.adultCount = 2;
                }
              } else {
                // searchResults yok — tarihsiz arama olarak kabul et
                this.isDatelessSearch = true;
              }

              // ✅ GetDetail API'sinden gelen addon'ları ekle ve transform et
              if (hotelData.details?.additionalAddons) {
                if (!pricingDetails) {
                  pricingDetails = {};
                }
                
                const addonsObj: any = hotelData.details.additionalAddons; // any cast - dynamic keys için
                
                // ⭐ KRİTİK: GÜNCEL PassengerKey'leri GetDetail response'ından al
                const updatedPassengerKeys = Object.keys(addonsObj);
                if (updatedPassengerKeys.length > 0) {
                  this.passengerKeys = updatedPassengerKeys;
                  this.searchService.updatePassengerKeys(updatedPassengerKeys);
                }

                // Backend'den passengerKey bazlı object olarak geliyor, unique key bazlı flat array'e çevir
                const addonMap = new Map<string, any>();
                
                Object.keys(addonsObj).forEach(passengerKey => {
                  const passengerAddons = addonsObj[passengerKey];
                  if (Array.isArray(passengerAddons)) {
                    passengerAddons.forEach((addon: any) => {
                      if (!addonMap.has(addon.key)) {
                        addonMap.set(addon.key, {
                          key: addon.key,
                          id: addon.addonId,
                          name: addon.name,
                          description: addon.remark || '',
                          price: addon.totalPrice,
                          isRequired: addon.isRequired || false,
                          isSelected: false,
                          addonType: addon.addonTypeValue,
                          startDate: addon.startDate,
                          endDate: addon.endDate
                        });
                      }
                    });
                  }
                });
                
                pricingDetails.additionalAddons = {
                  addons: Array.from(addonMap.values())
                };
                
                
                // TotalPrice da varsa ekle
                if (hotelData.details?.totalPrice) {
                  pricingDetails.totalPrice = hotelData.details.totalPrice;
                }
              }
              
              // ✅ Initialize addon selection: 
              // Backend'den additionalAddons.addons[] içinde isSelected flag ile gelir
              if (pricingDetails?.additionalAddons?.addons) {
                pricingDetails.additionalAddons.addons.forEach((addon: any) => {
                  // isRequired veya zaten isSelected ise true, değilse false
                  this.selectedAddons[addon.key] = addon.isRequired || addon.isSelected || false;
                });
                this.calculateTotalPrice(pricingDetails);
              } else {
              }
              
              // ⚠️ Minimum gece kısıtlaması kontrolü
              
              // ⭐ ÖNEMLI: Eğer SearchService'ten minNight kısıtlaması alındıysa, SIFIRLAMAYIZ!
              if (!this.hasMinNightRestriction && hotelData.details) {
                // items boş ama alternativeRoomGroup varsa -> min gece kısıtlaması
                // alternativeRoomGroup veya alternativeRoomGroupRoomGroups olabilir
                const altRoomGroup = hotelData.details.alternativeRoomGroup 
                  || hotelData.details.alternativeRoomGroups
                  || hotelData.details.alternativeRoomGroupRoomGroups;
                
                if ((!hotelData.details.items || hotelData.details.items.length === 0) 
                    && altRoomGroup?.length > 0) {
                  
                  
                  this.hasMinNightRestriction = true;
                  this.alternativeRoomGroups = altRoomGroup;
                  
                  // İlk alternatif gruptan bilgileri al
                  const firstAlt = altRoomGroup[0];
                  
                  // minNight değerini al
                  this.minNightCount = firstAlt.roomGroup?.minNight || 2;
                  
                  // Mesajı al (reasons veya messages'tan)
                  this.minNightMessage = firstAlt.reasons?.[0]?.text 
                    || firstAlt.roomGroup?.messages?.[0]?.text 
                    || `Bu otel minimum ${this.minNightCount} gece konaklama gerektirmektedir.`;
                  
                }
              } else if (this.hasMinNightRestriction) {
              }
              
              if (hotelData.details && hotelData.details.items && Array.isArray(hotelData.details.items)) {
                roomGroups = hotelData.details.items;

                // ⭐ items[].rooms boş ise: roomKeys + details.rooms hashmap'inden resolve et
                const topLevelRooms = hotelData.details.rooms;
                const isRoomsHashmap = topLevelRooms && typeof topLevelRooms === 'object' && !Array.isArray(topLevelRooms);

                roomGroups = roomGroups.map((rg: any) => {
                  if (rg.rooms && Array.isArray(rg.rooms) && rg.rooms.length > 0) return rg;

                  // roomKeys varsa hashmap'ten çöz
                  if (isRoomsHashmap && Array.isArray(rg.roomKeys) && rg.roomKeys.length > 0) {
                    const resolved = rg.roomKeys.map((k: any) => topLevelRooms[k]).filter(Boolean);
                    if (resolved.length > 0) return { ...rg, rooms: resolved };
                  }

                  // roomDetails inline object varsa
                  if (rg.roomDetails && typeof rg.roomDetails === 'object' && !Array.isArray(rg.roomDetails)) {
                    const resolved = Object.values(rg.roomDetails);
                    if (resolved.length > 0) return { ...rg, rooms: resolved };
                  }

                  // Array fallback
                  if (Array.isArray(topLevelRooms) && topLevelRooms.length > 0) {
                    return { ...rg, rooms: topLevelRooms };
                  }

                  return rg;
                });

                roomGroups = roomGroups.map(roomGroup => {
                  if (roomGroup.rooms && Array.isArray(roomGroup.rooms) && roomGroup.rooms.length > 0) {
                    const enrichedRooms = roomGroup.rooms.map((room: any) => {
                      let roomTypeText = '';
                      let facilities: any[] = [];
                      if (hotelData.details.hotel && hotelData.details.hotel.roomTypeInfos) {
                        const roomTypeInfo = hotelData.details.hotel.roomTypeInfos.find((info: any) => 
                          info.hotelRoomId.toString() === room.hotelRoomCode
                        );
                        if (roomTypeInfo) {
                          roomTypeText = roomTypeInfo.roomTypeText;
                          facilities = roomTypeInfo.facilities || [];
                        }
                      }
                      
                      let roomType = '';
                      let roomImages: any[] = [];
                      if (hotelData.details.roomDetails && room.roomDetailKey) {
                        const roomDetail = hotelData.details.roomDetails[room.roomDetailKey];
                        if (roomDetail) {
                          roomType = roomDetail.roomType;
                          roomImages = roomDetail.images || [];
                        }
                      }
                      
                      let roomAvailability = null;
                      if (parsedAvailability && parsedAvailability.length > 0) {
                        const roomKey = room.key || room.roomKey;
                        
                        // 1️⃣ Önce roomKey ile eşleştirmeyi dene (en güvenilir)
                        if (roomKey) {
                          roomAvailability = parsedAvailability.find((avail: any) => 
                            avail.roomKey === roomKey
                          );
                        }
                        
                        // 2️⃣ roomKey eşleşmezse roomType ile dene
                        if (!roomAvailability && roomTypeText) {
                        roomAvailability = parsedAvailability.find((avail: any) => 
                          avail.roomType === roomTypeText
                        );
                        }
                        
                        // 3️⃣ Hala eşleşmezse alternatif roomType ile dene
                        if (!roomAvailability && roomType) {
                          roomAvailability = parsedAvailability.find((avail: any) => 
                            avail.roomType === roomType
                          );
                        }
                        
                        if (roomAvailability) {
                        } else {
                        }
                      }
                      
                      // ⚠️ MinNight kısıtlaması varsa availability'yi override et
                      let finalAvailability = roomAvailability;
                      if (this.isDatelessSearch) {
                        // Tarihsiz aramada availability bilgisi olmaz - null bırak
                        finalAvailability = null;
                      } else if (this.hasMinNightRestriction) {
                        finalAvailability = {
                          ...(roomAvailability || {}),
                          hasAvailability: false,
                          minNightRestriction: true,
                          minNight: this.minNightCount
                        };
                      }
                      
                      return {
                        ...room,
                        roomTypeText,
                        roomType,
                        facilities,
                        roomImages,
                        availability: finalAvailability,
                        minNightRestricted: this.hasMinNightRestriction
                      };
                    });
                    
                    return {
                      ...roomGroup,
                      rooms: enrichedRooms
                    };
                  }
                  return roomGroup;
                });
              }
              
              // ⚠️ alternativeRoomGroup'tan odaları al (min gece kısıtlaması durumunda)
              const altRoomGroupData = hotelData.details?.alternativeRoomGroup 
                || hotelData.details?.alternativeRoomGroups
                || hotelData.details?.alternativeRoomGroupRoomGroups;
              
              if (roomGroups.length === 0 && altRoomGroupData?.length > 0) {
                
                // Her alternativeRoomGroup için roomGroup'u al
                // alternativeRoomGroup: { reasons, roomGroup } yapısında
                // alternativeRoomGroupRoomGroups: doğrudan roomGroup yapısında
                const altRoomGroups = altRoomGroupData.map((alt: any) => {
                  // roomGroup wrapper varsa onu kullan, yoksa doğrudan alt'ı kullan
                  const rg = alt.roomGroup || alt;
                  if (rg && rg.rooms && Array.isArray(rg.rooms)) {
                    const enrichedRooms = rg.rooms.map((room: any) => {
                      let roomTypeText = '';
                      let facilities: any[] = [];
                      if (hotelData.details.hotel && hotelData.details.hotel.roomTypeInfos) {
                        const roomTypeInfo = hotelData.details.hotel.roomTypeInfos.find((info: any) => 
                          info.hotelRoomId?.toString() === room.hotelRoomCode
                        );
                        if (roomTypeInfo) {
                          roomTypeText = roomTypeInfo.roomTypeText;
                          facilities = roomTypeInfo.facilities || [];
                        }
                      }
                      
                      let roomType = '';
                      let roomImages: any[] = [];
                      if (hotelData.details.roomDetails && room.roomDetailKey) {
                        const roomDetail = hotelData.details.roomDetails[room.roomDetailKey];
                        if (roomDetail) {
                          roomType = roomDetail.roomType;
                          roomImages = roomDetail.images || [];
                        }
                      }
                      
                      return {
                        ...room,
                        roomTypeText,
                        roomType,
                        facilities,
                        roomImages,
                        // ⚠️ Min gece kısıtlaması - availability false olarak işaretle
                        availability: {
                          hasAvailability: false,
                          minNightRestriction: true,
                          minNight: rg.minNight || this.minNightCount
                        },
                        // Fiyat bilgisi alternativeRoomGroup'tan
                        minNightRestricted: true
                      };
                    });
                    
                    return {
                      ...rg,
                      rooms: enrichedRooms,
                      minNightRestricted: true
                    };
                  }
                  return rg;
                });
                
                // Unique roomGroups (aynı oda tiplerini birleştir)
                const uniqueRoomGroups = new Map<string, any>();
                altRoomGroups.forEach((rg: any) => {
                  if (rg && rg.rooms) {
                    rg.rooms.forEach((room: any) => {
                      const key = `${room.hotelRoomCode}_${room.mealType}`;
                      if (!uniqueRoomGroups.has(key)) {
                        uniqueRoomGroups.set(key, {
                          ...rg,
                          rooms: [room]
                        });
                      }
                    });
                  }
                });
                
                roomGroups = Array.from(uniqueRoomGroups.values());
                
                // Debug: İlk odanın bilgilerini göster
                if (roomGroups.length > 0 && roomGroups[0].rooms?.length > 0) {
                  const firstRoom = roomGroups[0].rooms[0];
                }
              }
              
              // ⭐ Tarihsiz arama: odalar boşsa roomTypeInfos'dan oluştur
              const hasAnyRooms = roomGroups.some((rg: any) => rg.rooms?.length > 0);
              if (!hasAnyRooms && this.isDatelessSearch && hotelData.details?.hotel?.roomTypeInfos?.length > 0) {
                const roomTypeInfos = hotelData.details.hotel.roomTypeInfos;

                roomGroups = roomTypeInfos.map((roomInfo: any) => {
                  let roomImages: any[] = roomInfo.images || [];

                  // roomDetails'dan ek bilgi almayı dene
                  let roomType = roomInfo.roomTypeText || '';
                  if (hotelData.details.roomDetails) {
                    const matchingDetail = Object.values(hotelData.details.roomDetails).find(
                      (rd: any) => rd.hotelRoomCode === roomInfo.hotelRoomId?.toString()
                    );
                    if (matchingDetail) {
                      roomType = (matchingDetail as any).roomType || roomType;
                      if ((matchingDetail as any).images?.length > 0) {
                        roomImages = (matchingDetail as any).images;
                      }
                    }
                  }

                  return {
                    key: null,
                    rooms: [{
                      hotelRoomCode: roomInfo.hotelRoomId?.toString(),
                      roomTypeText: roomInfo.roomTypeText || '',
                      roomType: roomType,
                      facilities: roomInfo.facilities || [],
                      roomImages: roomImages,
                      mealType: '',
                      totalPrice: null,
                      availability: null,
                      key: null
                    }]
                  };
                });

              }

              // Final debug log
              if (roomGroups.length > 0) {
                if (roomGroups[0].rooms?.[0]?.availability) {
                }
              }
              
              return {
                details: hotelData.details,
                periods: [] as any[],
                firstPrice: null as number | null,
                mapUrl: mapUrl,
                availability: availabilityData,
                parsedAvailability: parsedAvailability,
                roomGroups: roomGroups,
                pricingDetails: pricingDetails
              };
            })
          );
        } else {
          this.router.navigate(['/']);
          return of({ details: null, periods: [], firstPrice: null, mapUrl: null, availability: [], parsedAvailability: [], roomGroups: [], pricingDetails: null });
        }
      }),
      // Son veriyi cache'le (goToPassengerInfo senkron erişim için)
      tap(data => {
        this._cachedHotelData = data;
      }),
      // ⭐ KRİTİK: shareReplay ile cache'le - çoklu subscription'da aynı sonuç dönsün
      shareReplay(1)
    );
  }

  // goToPassengerInfo'da senkron erişim için cache
  private _cachedHotelData: any = null;

  openMapModal(): void {
    this.mapLoaded = false;
    this.isMapModalOpen = true;
  }

  closeMapModal(): void {
    this.isMapModalOpen = false;
    this.mapLoaded = false;
  }

  onMapLoad(): void {
    this.mapLoaded = true;
  }

  openGalleryModal(index: number): void {
    this.currentImageIndex = index;
    this.isGalleryModalOpen = true;
  }

  closeGalleryModal(): void {
    this.isGalleryModalOpen = false;
  }

  nextImage(images: any[]): void {
    this.currentImageIndex = (this.currentImageIndex + 1) % images.length;
  }

  prevImage(images: any[]): void {
    this.currentImageIndex = (this.currentImageIndex - 1 + images.length) % images.length;
  }

  // Oda Galeri Modal Metodları
  openRoomGalleryModal(images: any[], index: number = 0): void {
    if (images && images.length > 0) {
      this.currentRoomImages = images;
      this.currentRoomImageIndex = index;
      this.isRoomGalleryModalOpen = true;
    }
  }

  closeRoomGalleryModal(): void {
    this.isRoomGalleryModalOpen = false;
    this.currentRoomImages = [];
    this.currentRoomImageIndex = 0;
  }

  nextRoomImage(): void {
    if (this.currentRoomImages.length > 0) {
      this.currentRoomImageIndex = (this.currentRoomImageIndex + 1) % this.currentRoomImages.length;
    }
  }

  prevRoomImage(): void {
    if (this.currentRoomImages.length > 0) {
      this.currentRoomImageIndex = (this.currentRoomImageIndex - 1 + this.currentRoomImages.length) % this.currentRoomImages.length;
    }
  }

  toggleFacilities(): void {
    this.showAllFacilities = !this.showAllFacilities;
  }

  /**
   * Benzersiz yemek seçeneklerini döndürür (tekrarları kaldırır)
   */
  getUniqueMealTypes(mealTypeInfos: any[]): any[] {
    if (!mealTypeInfos || mealTypeInfos.length === 0) {
      return [];
    }
    
    const seen = new Set<string>();
    return mealTypeInfos.filter(mealInfo => {
      const mealText = mealInfo.mealTypeText || mealInfo.mealType || '';
      if (seen.has(mealText)) {
        return false;
      }
      seen.add(mealText);
      return true;
    });
  }

  toggleAvailabilityDetails(roomKey: string): void {
    this.showAvailabilityDetails[roomKey] = !this.showAvailabilityDetails[roomKey];
  }

  toggleRoomFacilities(roomKey: string): void {
    this.showAllRoomFacilities[roomKey] = !this.showAllRoomFacilities[roomKey];
  }

  getRoomFacilitiesToShow(room: any): any[] {
    const maxVisible = 6;
    const roomKey = room.key || room.roomKey || room.hotelRoomCode || '';
    const isExpanded = this.showAllRoomFacilities[roomKey];
    
    if (!room.facilities || room.facilities.length <= maxVisible) {
      return room.facilities || [];
    }
    
    return isExpanded ? room.facilities : room.facilities.slice(0, maxVisible);
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getStatusText(status: number): string {
    switch (status) {
      case 0: return 'Müsait - Satış Yapılabilir';
      case 4: return 'Kontenjan Yok';
      default: return `Durum: ${status}`;
    }
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 0: return 'text-green-2';
      case 4: return 'text-red-2';
      default: return 'text-yellow-3';
    }
  }

  goToHome(): void {
    this.router.navigate(['/']);
  }

  toggleAddon(addonKey: string, event: any, pricingDetails: any): void {
    const isSelected = event.target.checked;
    
    
    // PassengerKey kontrolü
    if (!this.passengerKeys || this.passengerKeys.length === 0) {
      this.errorMessage = 'Bir sorun oluştu. Lütfen sayfayı yenileyip tekrar deneyin.';
      event.target.checked = !isSelected;
      return;
    }
    
    // ProductKey kontrolü (RoomGroup.Key veya güncel ProductKey)
    if (!this.productKey) {
      this.errorMessage = 'Oda bilgisi yüklenemedi. Lütfen sayfayı yenileyiniz.';
      event.target.checked = !isSelected;
      return;
    }
    
    
    // ✅ TÜM yolcular için operations oluştur (v3.0: Çoklu yolcu desteği)
    const operations = this.passengerKeys.map(passengerKey => ({
      addOnKey: addonKey,
      passengerKey: passengerKey,
      type: isSelected ? 'Add' as const : 'Remove' as const
    }));
    
    
    // ⭐ TraceId'yi component'ten al (GetDetail response'ından gelen)
    
    if (!this.traceId) {
      this.errorMessage = 'Oturumunuzun süresi dolmuş. Lütfen sayfayı yenileyip tekrar deneyin.';
      event.target.checked = !isSelected;
      return;
    }
    
    // Loading state'i başlat
    this.isAddonProcessing[addonKey] = true;
    this.errorMessage = null;
    
    if (isSelected) {
      // ✅ Addon EKLE - Backend API çağrısı (TÜM YOLCULAR İÇİN)
      
      // ⭐ manageAddOn kullan - rawResponse'dan ProductKey otomatik parse edilir
      // ⭐ TraceId component'ten al (GetDetail response'ından gelen)
      this.hotelService.manageAddOn({
        productKey: this.productKey,
        traceId: this.traceId, // ⭐ GetDetail'den gelen TraceId
        operations: operations
      })
        .pipe(
          finalize(() => {
            this.isAddonProcessing[addonKey] = false;
            this.cdr.detectChanges(); // ✅ Force UI update
          }),
          tap(response => {
          }),
          catchError(error => {
            
            this.errorMessage = 'Ek hizmet eklenirken bir sorun oluştu. Lütfen tekrar deneyin.';
            event.target.checked = false;
            return of(null);
          })
        )
        .subscribe({
          next: (response: any) => {
            // catchError'dan null dönerse burayı atla
            if (!response) {
              return;
            }

            if (response.success !== false) {
              this.selectedAddons[addonKey] = true;
              
              // ⭐ ZORUNLU: ProductKey'i güncelle (Backend her işlemde yeni key üretebilir)
              if (response.productKey) {
                const oldKey = this.productKey;
                this.productKey = response.productKey;
              } else {
              }
              
              // Backend'den gelen güncel fiyatı kullan
              // v3.0: totalPrice, basketLeastwiseAmount veya balance'dan al
              let updatedPrice: number | null = null;
              
              if (response.totalPrice) {
                updatedPrice = typeof response.totalPrice === 'number' 
                  ? response.totalPrice 
                  : response.totalPrice.value;
              } else if (response.basketLeastwiseAmount) {
                updatedPrice = typeof response.basketLeastwiseAmount === 'number'
                  ? response.basketLeastwiseAmount
                  : response.basketLeastwiseAmount.value;
              } else if (response.balance) {
                updatedPrice = typeof response.balance === 'number'
                  ? response.balance
                  : response.balance.value;
              }
              
              if (updatedPrice) {
                this.currentTotalPrice = updatedPrice;
              }
              
              // ✅ Backend v3.0: additionalAddons passengerKey bazlı geliyor
              if (response.additionalAddons) {
                const addonsObj: any = response.additionalAddons;
                
                // ⭐ KRİTİK: Addon işleminden sonra da PassengerKey'leri güncelle
                const updatedPassengerKeys = Object.keys(addonsObj);
                if (updatedPassengerKeys.length > 0) {
                  const oldKeys = [...this.passengerKeys];
                  this.passengerKeys = updatedPassengerKeys;
                  // ⭐ SearchService'i de güncelle
                  this.searchService.updatePassengerKeys(updatedPassengerKeys);
                }

                // Tüm addon'ları kontrol et ve isSelected durumunu güncelle
                Object.keys(addonsObj).forEach(passengerKey => {
                  const passengerAddons = addonsObj[passengerKey];
                  if (Array.isArray(passengerAddons)) {
                    passengerAddons.forEach((addon: any) => {
                      // Backend'den isSelected flag'i yoksa, eklediğimiz için true yap
                      this.selectedAddons[addon.key] = true;
                    });
                  }
                });
              }
              
              // Backend message varsa göster
              if (response.message) {
              }
            } else {
              event.target.checked = false;
              this.errorMessage = 'Ek hizmet eklenirken bir sorun oluştu. Lütfen tekrar deneyin.';
            }
          },
          error: (err: any) => {
          }
        });

    } else {
      // ✅ Addon ÇIKAR

      this.hotelService.manageAddOn({
        productKey: this.productKey,
        traceId: this.traceId,
        operations: operations
      })
        .pipe(
          finalize(() => {
            this.isAddonProcessing[addonKey] = false;
            this.cdr.detectChanges();
          }),
          tap(response => {
          }),
          catchError(error => {
            this.errorMessage = 'Ek hizmet kaldırılırken bir sorun oluştu. Lütfen tekrar deneyin.';
            event.target.checked = true;
            return of(null);
          })
        )
        .subscribe({
          next: (response: any) => {
            // catchError'dan null dönerse burayı atla
            if (!response) {
              return;
            }
            
            if (response.success !== false) {
              this.selectedAddons[addonKey] = false;
              
              // ⭐ ZORUNLU: ProductKey'i güncelle (Backend her işlemde yeni key üretebilir)
              if (response.productKey) {
                const oldKey = this.productKey;
                this.productKey = response.productKey;
              } else {
              }
              
              // Backend'den gelen güncel fiyatı kullan
              // v3.0: totalPrice, basketLeastwiseAmount veya balance'dan al
              let updatedPrice: number | null = null;
              
              if (response.totalPrice) {
                updatedPrice = typeof response.totalPrice === 'number' 
                  ? response.totalPrice 
                  : response.totalPrice.value;
              } else if (response.basketLeastwiseAmount) {
                updatedPrice = typeof response.basketLeastwiseAmount === 'number'
                  ? response.basketLeastwiseAmount
                  : response.basketLeastwiseAmount.value;
              } else if (response.balance) {
                updatedPrice = typeof response.balance === 'number'
                  ? response.balance
                  : response.balance.value;
              }
              
              if (updatedPrice) {
                this.currentTotalPrice = updatedPrice;
              }
              
              // ✅ Backend v3.0: Remove sonrası addon listesi güncellendi
              // Eğer response'ta additionalAddons varsa, durumları sync et
              if (response.additionalAddons) {
                const addonsObj: any = response.additionalAddons;
                
                // ⭐ KRİTİK: Addon çıkarma işleminden sonra da PassengerKey'leri güncelle
                const updatedPassengerKeys = Object.keys(addonsObj);
                if (updatedPassengerKeys.length > 0) {
                  const oldKeys = [...this.passengerKeys];
                  this.passengerKeys = updatedPassengerKeys;
                  // ⭐ SearchService'i de güncelle
                  this.searchService.updatePassengerKeys(updatedPassengerKeys);
                }

                // Bu addon'u tüm passenger'lar için kontrol et
                Object.keys(addonsObj).forEach(passengerKey => {
                  const passengerAddons = addonsObj[passengerKey];
                  if (Array.isArray(passengerAddons)) {
                    // Bu passengerKey'deki addon'lardan bizimki var mı?
                    const stillExists = passengerAddons.find((a: any) => a.key === addonKey);
                    if (!stillExists) {
                      this.selectedAddons[addonKey] = false;
                    }
                  }
                });
              } else {
                // additionalAddons yoksa, direkt false yap
                this.selectedAddons[addonKey] = false;
              }
              
              
              // Backend message varsa göster
              if (response.message) {
              }
            } else {
              event.target.checked = true;
              this.errorMessage = 'Ek hizmet kaldırılırken bir sorun oluştu. Lütfen tekrar deneyin.';
            }
          },
          error: (err: any) => {
            // catchError zaten handle etti ama extra güvenlik
          }
        });
    }
  }

  private calculateTotalPrice(pricingDetails: any): void {
    if (!pricingDetails) return;
    
    // ✅ Backend'den gelen totalPrice'ı kullan (addon'lar dahil)
    // v3.0: totalPrice hem number hem object olabilir
    if (pricingDetails.totalPrice) {
      if (typeof pricingDetails.totalPrice === 'number') {
        this.currentTotalPrice = pricingDetails.totalPrice;
      } else if (pricingDetails.totalPrice.value) {
        this.currentTotalPrice = pricingDetails.totalPrice.value;
      }
      return;
    }
    
    // Fallback: Manuel hesaplama
    let totalPrice = pricingDetails.basePrice?.value || 0;
    
    // Seçili addon'ların fiyatlarını hesapla
    const selectedAddonKeys = Object.keys(this.selectedAddons).filter(key => this.selectedAddons[key]);
    
    // ✅ additionalAddons.addons[] içinden addon bilgilerini al
    if (pricingDetails.additionalAddons?.addons && selectedAddonKeys.length > 0) {
      selectedAddonKeys.forEach(addonKey => {
        const addon = pricingDetails.additionalAddons.addons.find((a: any) => a.key === addonKey);
        if (addon && addon.price?.value) {
          totalPrice += addon.price.value;
        }
      });
    }
    
    this.currentTotalPrice = totalPrice;
  }

  getAddonPrice(addon: any): number {
    // ✅ v3.0: Addon fiyatı × yolcu sayısı
    let basePrice = 0;
    
    if (addon.price?.value !== undefined) {
      basePrice = addon.price.value;
    } else if (addon.amount?.value !== undefined) {
      basePrice = addon.amount.value;
    }
    
    // Çoklu yolcu için toplam fiyat
    const passengerCount = this.passengerKeys?.length || 1;
    const totalPrice = basePrice * passengerCount;
    
    
    return totalPrice;
  }

  /**
   * Yolcu bilgileri sayfasına yönlendir
   * @param roomKey Oda key'i
   * @param originalProductKey Orijinal ürün key'i (HTML'den gelen - kullanılmayacak)
   */
  goToPassengerInfo(roomKey: string, originalProductKey: string): void {
    const finalProductKey = this.productKey;

    if (!finalProductKey) {
      this.errorMessage = 'Oda bilgisi yüklenemedi. Lütfen sayfayı yenileyin.';
      return;
    }

    const data = this._cachedHotelData;

    // Search'ten gelen passenger key'leri kullan (validate API bunları bekliyor)
    const searchKeys = this.searchService.getPassengerKeys();

    // Senkron olarak güncel verileri kaydet
    this.searchService.setAddonData({
      addons: data?.pricingDetails?.additionalAddons?.addons || [],
      basePrice: data?.pricingDetails?.basePrice,
      totalPrice: data?.pricingDetails?.totalPrice || data?.pricingDetails?.basePrice,
      productKey: finalProductKey,
      traceId: this.traceId,
      passengerKeys: searchKeys.length > 0 ? searchKeys : [...this.passengerKeys]
    });

    const hotel = data?.details?.hotel;
    const selectedRoom = data?.roomGroups
      ?.flatMap((rg: any) => rg.rooms || [])
      ?.find((r: any) => r.key === roomKey);
    const firstRoom = selectedRoom || data?.roomGroups?.[0]?.rooms?.[0];

    this.searchService.setBookingSummary({
      hotelName: hotel?.name || '',
      hotelImage: hotel?.images?.[0]?.url || '',
      hotelStars: hotel?.hotelCategoryId || 0,
      roomType: firstRoom?.roomTypeText || firstRoom?.roomType || '',
      mealType: firstRoom?.mealType || '',
      checkIn: this.searchCheckInDate || '',
      checkOut: this.searchCheckOutDate || '',
      adultCount: this.adultCount,
      childCount: this.childCount,
      roomCount: this.roomCount
    });

    this.router.navigate(['/yolcu-bilgileri'], {
      queryParams: {
        productKey: finalProductKey,
        roomKey: roomKey
      }
    });
  }

  getComboTotalPrice(roomGroup: any): number {
    if (!roomGroup?.rooms) return 0;
    return roomGroup.rooms.reduce((sum: number, room: any) => sum + (room.totalPrice?.value || 0), 0);
  }

  get roomPositions(): number[] {
    return Array.from({ length: this.roomCount }, (_, i) => i);
  }

  private getRoomIdentifier(room: any): string {
    return (room.roomTypeText || room.roomType || '') + '|' + (room.mealType || '');
  }

  getUniqueRoomsForPosition(position: number, roomGroups: any[]): any[] {
    if (!roomGroups) return [];
    const seen = new Map<string, any>();
    for (const group of roomGroups) {
      const room = group.rooms?.[position];
      if (!room) continue;
      const id = this.getRoomIdentifier(room);
      if (!seen.has(id)) {
        seen.set(id, { ...room, _identifier: id });
      }
    }
    return Array.from(seen.values());
  }

  selectRoomForPosition(position: number, identifier: string, roomGroups: any[]): void {
    this.selectedRoomByPosition[position] = identifier;
    this.matchedGroupIndex = this.findMatchingGroup(roomGroups);
  }

  isRoomSelectedForPosition(position: number, identifier: string): boolean {
    return this.selectedRoomByPosition[position] === identifier;
  }

  private findMatchingGroup(roomGroups: any[]): number {
    if (!roomGroups) return -1;
    const selectedCount = Object.keys(this.selectedRoomByPosition).length;
    if (selectedCount < this.roomCount) return -1;

    for (let gi = 0; gi < roomGroups.length; gi++) {
      const group = roomGroups[gi];
      let allMatch = true;
      for (let pos = 0; pos < this.roomCount; pos++) {
        const room = group.rooms?.[pos];
        if (!room) { allMatch = false; break; }
        if (this.getRoomIdentifier(room) !== this.selectedRoomByPosition[pos]) {
          allMatch = false;
          break;
        }
      }
      if (allMatch) return gi;
    }
    return -1;
  }

  getRoomsToShowForPosition(position: number, roomGroups: any[]): any[] {
    const all = this.getUniqueRoomsForPosition(position, roomGroups);
    if (this.showAllRoomsForPosition[position]) return all;
    return all.slice(0, this.multiRoomInitialCount);
  }

  toggleShowAllRoomsForPosition(position: number): void {
    this.showAllRoomsForPosition[position] = !this.showAllRoomsForPosition[position];
  }

  getMatchedComboTotal(roomGroups: any[]): number {
    if (this.matchedGroupIndex < 0 || !roomGroups?.[this.matchedGroupIndex]) return 0;
    return this.getComboTotalPrice(roomGroups[this.matchedGroupIndex]);
  }

  /**
   * Arama isteği için rooms dizisi oluşturur.
   * Çoklu oda desteği: yetişkinler odalara eşit dağıtılır, çocuklar 1. odaya eklenir.
   */
  private buildSearchRooms(): { passengers: any[] }[] {
    const rooms: { passengers: any[] }[] = [];
    const adultsPerRoom = Math.floor(this.adultCount / this.roomCount);
    let remainingAdults = this.adultCount % this.roomCount;

    for (let r = 0; r < this.roomCount; r++) {
      const passengers: any[] = [];
      let roomAdults = adultsPerRoom + (remainingAdults > 0 ? 1 : 0);
      if (remainingAdults > 0) remainingAdults--;

      for (let a = 0; a < roomAdults; a++) {
        passengers.push({ age: 30, birthday: null, gender: null, passengerType: 0 });
      }

      if (r === 0) {
        for (let c = 0; c < this.childCount; c++) {
          passengers.push({ age: this.childrenAges[c] || 5, birthday: null, gender: null, passengerType: 1 });
        }
      }

      rooms.push({ passengers });
    }

    return rooms;
  }

  /**
   * Minimum gece kısıtlaması durumunda arama sayfasına geri dön
   */
  goBackToSearch(): void {
    
    // Ana sayfaya yönlendir (arama formu orada)
    this.router.navigate(['/']);
  }

  /**
   * Misafir seçiciyi aç/kapat
   */
  toggleGuestSelector(): void {
    this.isGuestSelectorOpen = !this.isGuestSelectorOpen;
    if (this.isGuestSelectorOpen) {
      this.isMinNightCalendarOpen = false; // Takvimi kapat
    }
  }

  /**
   * Misafir sayısını güncelle
   */
  updateGuestCount(type: 'adults' | 'children' | 'rooms', increment: boolean): void {
    let currentValue: number;
    let min: number;
    let max: number;
    
    switch (type) {
      case 'adults':
        currentValue = this.adultCount;
        min = 1;
        max = 10;
        break;
      case 'children':
        currentValue = this.childCount;
        min = 0;
        max = 6;
        break;
      case 'rooms':
        currentValue = this.roomCount;
        min = 1;
        max = 4;
        break;
    }
    
    const newValue = increment ? currentValue + 1 : currentValue - 1;
    
    if (newValue >= min && newValue <= max) {
      switch (type) {
        case 'adults':
          this.adultCount = newValue;
          break;
        case 'children':
          this.childCount = newValue;
          this.updateChildrenAgesArray(newValue);
          break;
        case 'rooms':
          this.roomCount = newValue;
          break;
      }
    }
  }

  /**
   * Çocuk yaşları dizisini güncelle
   */
  private updateChildrenAgesArray(childrenCount: number): void {
    if (childrenCount > this.childrenAges.length) {
      // Yeni çocuklar ekle (varsayılan yaş 5)
      for (let i = this.childrenAges.length; i < childrenCount; i++) {
        this.childrenAges.push(5);
      }
    } else if (childrenCount < this.childrenAges.length) {
      // Fazla çocukları sil
      this.childrenAges = this.childrenAges.slice(0, childrenCount);
    }
  }

  /**
   * Çocuk yaşını güncelle
   */
  updateChildAge(index: number, age: number): void {
    if (index >= 0 && index < this.childrenAges.length) {
      this.childrenAges[index] = age;
    }
  }

  /**
   * Minimum gece takvimini aç/kapat
   */
  toggleMinNightCalendar(): void {
    this.isMinNightCalendarOpen = !this.isMinNightCalendarOpen;
    if (this.isMinNightCalendarOpen) {
      this.isGuestSelectorOpen = false; // Misafir seçiciyi kapat
      this.inlineCalendarMonth = new Date();
      this.inlineCalendarSelectionStart = null;
      this.inlineCalendarSelectionEnd = null;
      this.generateInlineCalendarDays();
    }
  }

  scrollToDateSelection(): void {
    const el = document.getElementById('dateSelectionBar');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.isMobileSearchExpanded = true;
    if (!this.isMinNightCalendarOpen) {
      this.toggleMinNightCalendar();
    }
  }
  
  getCalendarMonthYear(): string {
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    return `${months[this.inlineCalendarMonth.getMonth()]} ${this.inlineCalendarMonth.getFullYear()}`;
  }
  
  prevCalendarMonth(): void {
    this.inlineCalendarMonth = new Date(this.inlineCalendarMonth.getFullYear(), this.inlineCalendarMonth.getMonth() - 1, 1);
    this.generateInlineCalendarDays();
  }
  
  nextCalendarMonth(): void {
    this.inlineCalendarMonth = new Date(this.inlineCalendarMonth.getFullYear(), this.inlineCalendarMonth.getMonth() + 1, 1);
    this.generateInlineCalendarDays();
  }
  
  generateInlineCalendarDays(): void {
    const year = this.inlineCalendarMonth.getFullYear();
    const month = this.inlineCalendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Haftanın hangi gününde başlıyor (0=Pazar, 1=Pazartesi, ...)
    let startDayOfWeek = firstDay.getDay();
    // Pazartesi'yi 0 yap
    startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    
    this.inlineCalendarDays = [];
    
    // Önceki ayın günleri
    const prevMonth = new Date(year, month, 0);
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      this.inlineCalendarDays.push({
        dayNumber: prevMonth.getDate() - i,
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false,
        isPast: true,
        isSelected: false,
        isInRange: false,
        isStart: false,
        isEnd: false
      });
    }
    
    // Bu ayın günleri
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const isPast = date < today;
      const isSelected = this.isDateSelected(date);
      const isInRange = this.isDateInRange(date);
      const isStart = this.inlineCalendarSelectionStart && date.getTime() === this.inlineCalendarSelectionStart.getTime();
      const isEnd = this.inlineCalendarSelectionEnd && date.getTime() === this.inlineCalendarSelectionEnd.getTime();
      
      this.inlineCalendarDays.push({
        dayNumber: day,
        date: date,
        isCurrentMonth: true,
        isPast: isPast,
        isSelected: isSelected,
        isInRange: isInRange,
        isStart: isStart,
        isEnd: isEnd
      });
    }
  }
  
  isDateSelected(date: Date): boolean {
    if (!this.inlineCalendarSelectionStart && !this.inlineCalendarSelectionEnd) return false;
    if (this.inlineCalendarSelectionStart && date.getTime() === this.inlineCalendarSelectionStart.getTime()) return true;
    if (this.inlineCalendarSelectionEnd && date.getTime() === this.inlineCalendarSelectionEnd.getTime()) return true;
    return false;
  }
  
  isDateInRange(date: Date): boolean {
    if (!this.inlineCalendarSelectionStart || !this.inlineCalendarSelectionEnd) return false;
    return date > this.inlineCalendarSelectionStart && date < this.inlineCalendarSelectionEnd;
  }
  
  onInlineCalendarDayClick(day: any): void {
    if (day.isPast || !day.isCurrentMonth) return;
    
    if (!this.inlineCalendarSelectionStart || (this.inlineCalendarSelectionStart && this.inlineCalendarSelectionEnd)) {
      // Başlangıç tarihi seç
      this.inlineCalendarSelectionStart = day.date;
      this.inlineCalendarSelectionEnd = null;
    } else {
      // Bitiş tarihi seç - inlineCalendarSelectionStart burada kesinlikle null değil
      const startDate = this.inlineCalendarSelectionStart!;
      
      if (day.date <= startDate) {
        // Eğer seçilen tarih başlangıçtan küçükse, başlangıç olarak ata
        this.inlineCalendarSelectionStart = day.date;
      } else {
        const endDate: Date = day.date;
        this.inlineCalendarSelectionEnd = endDate;
        
        // Gece sayısını kontrol et
        const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (this.hasMinNightRestriction && nights < this.minNightCount) {
          // Minimum gece şartı karşılanmıyor, bitiş tarihini sıfırla
          this.inlineCalendarSelectionEnd = null;
          alert(`Lütfen en az ${this.minNightCount} gece seçin. Seçtiğiniz: ${nights} gece`);
        } else {
          // Başarılı seçim - sadece tarihleri güncelle, arama "Ara" butonuyla yapılsın
          this.minNightCheckInDate = startDate;
          this.minNightCheckOutDate = this.inlineCalendarSelectionEnd;
          // Takvimi kapat
          this.isMinNightCalendarOpen = false;
        }
      }
    }
    
    this.generateInlineCalendarDays();
  }

  /**
   * Takvimde tarih seçildiğinde çağrılır
   */
  onMinNightDateSelected(event: { startDate: Date; endDate: Date }): void {
    
    this.minNightCheckInDate = event.startDate;
    this.minNightCheckOutDate = event.endDate;
    this.isMinNightCalendarOpen = false;
    
    // Gece sayısını hesapla
    const nights = Math.ceil((event.endDate.getTime() - event.startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Minimum gece kontrolü
    if (nights < this.minNightCount) {
      return;
    }
    
    // Yeni arama yap
    this.performNewSearch(event.startDate, event.endDate);
  }
  
  /**
   * Ara butonuna tıklandığında çağrılır
   */
  searchWithCurrentDates(): void {
    if (this.inlineCalendarSelectionStart && this.inlineCalendarSelectionEnd) {
      this.onMinNightDateSelected({
        startDate: this.inlineCalendarSelectionStart,
        endDate: this.inlineCalendarSelectionEnd
      });
    } else if (this.minNightCheckInDate && this.minNightCheckOutDate) {
      // Mevcut tarihlerle ara
      this.performNewSearch(this.minNightCheckInDate, this.minNightCheckOutDate);
    }
  }

  /**
   * Availability verilerini arka planda yükler - sayfa render'ını bloklamaz
   */
  private loadAvailabilityLazy(
    requests: { productKey: string, roomKey: string }[],
    searchResults: any
  ): void {
    if (requests.length === 0) return;

    const availabilityObservables = requests.map(req =>
      this.hotelService.getAvailability(req.productKey, req.roomKey).pipe(
        catchError(() => of(null))
      )
    );

    forkJoin(availabilityObservables).subscribe(availabilityData => {
      if (!availabilityData || availabilityData.length === 0) return;

      let checkInDate: Date | undefined;
      let checkOutDate: Date | undefined;
      if (searchResults?.searchCriteria) {
        if (searchResults.searchCriteria.checkInDate) {
          checkInDate = new Date(searchResults.searchCriteria.checkInDate);
        }
        if (searchResults.searchCriteria.checkOutDate) {
          checkOutDate = new Date(searchResults.searchCriteria.checkOutDate);
        }
      }

      const parsedAvailability = this.hotelService.parseAvailabilityData(
        availabilityData.filter(Boolean),
        checkInDate,
        checkOutDate
      );

      if (parsedAvailability.length === 0) return;

      // Mevcut observable'ın son değerini güncelle
      this.hotelWithPeriods$?.pipe(
        tap(data => {
          if (data?.roomGroups) {
            data.roomGroups.forEach((roomGroup: any) => {
              if (roomGroup.rooms && Array.isArray(roomGroup.rooms)) {
                roomGroup.rooms.forEach((room: any) => {
                  const roomKey = room.key || room.roomKey;
                  const roomTypeText = room.roomTypeText;
                  const roomType = room.roomType;

                  let match = roomKey
                    ? parsedAvailability.find((a: any) => a.roomKey === roomKey)
                    : null;
                  if (!match && roomTypeText) {
                    match = parsedAvailability.find((a: any) => a.roomType === roomTypeText);
                  }
                  if (!match && roomType) {
                    match = parsedAvailability.find((a: any) => a.roomType === roomType);
                  }

                  if (match) {
                    if (this.hasMinNightRestriction) {
                      room.availability = {
                        ...match,
                        hasAvailability: false,
                        minNightRestriction: true,
                        minNight: this.minNightCount
                      };
                    } else {
                      room.availability = match;
                    }
                  }
                });
              }
            });
            data.parsedAvailability = parsedAvailability;
          }
        })
      ).subscribe(() => {
        this.cdr.detectChanges();
      });
    });
  }

  /**
   * Yeni tarihlerle arama yapar
   */
  private performNewSearch(checkIn: Date, checkOut: Date): void {
    this.selectedRoomByPosition = {};
    this.matchedGroupIndex = -1;
    this.showAllRoomsForPosition = {};
    
    const currentCriteria = this.searchService.getUserSearchCriteria();
    
    if (!this.currentHotelId) {
      return;
    }
    
    // ⭐ Tarihleri düzgün ayarla (timezone sorunu için)
    const adjustedCheckIn = new Date(checkIn);
    adjustedCheckIn.setHours(0, 0, 0, 0);
    
    const adjustedCheckOut = new Date(checkOut);
    adjustedCheckOut.setHours(0, 0, 0, 0);
    
    
    // SearchRequest oluştur
    // Otel araması için: destinationId: 0, hotelId: otelId, locationType: 5 (Hotel)
    const hotelIdNum = parseInt(this.currentHotelId, 10) || 0;
    
    // Odaları oluştur (çoklu oda desteği)
    const rooms = this.buildSearchRooms();
    
    
    const searchRequest: any = {
      currency: 'TRY',
      checkInDate: toLocalISOString(adjustedCheckIn),
      checkOutDate: toLocalISOString(adjustedCheckOut),
      countryCode: 'TR',
      destinations: [{
        destinationId: 0,
        destinationLabel: '',
        destinationParentId: 0,
        locationType: 5,
        hotelId: hotelIdNum
      }],
      pageNumber: 1,
      pageSize: 10,
      rooms,
      includeRoomPrices: true,
    };

    // Kullanıcı arama kriterlerini güncelle (tarihler + misafir sayıları)
    const newCriteria = {
      ...(currentCriteria || {}),
      checkInDate: adjustedCheckIn,
      checkOutDate: adjustedCheckOut,
      adults: this.adultCount,
      children: this.childCount,
      rooms: this.roomCount,
      childrenAges: [...this.childrenAges],
      hotelId: this.currentHotelId
    } as any;
    this.searchService.setUserSearchCriteria(newCriteria);
    
    // Tarih seçildi, artık tarihsiz arama değil
    this.isDatelessSearch = false;

    // Loading göster
    this.isSearching = true;

    // ⭐ KRİTİK: Yeni arama öncesi eski passenger key'leri temizle
    this.searchService.clearPassengerState();

    // API'ye istek at
    this.searchService.searchHotels(searchRequest).subscribe({
      next: (response: any) => {
        // Sonuçları kaydet (passengerKeys items[].rooms[].passengerKeys'ten çıkarılır)
        this.searchService.setSearchResults(response);
        this.passengerKeys = this.searchService.getPassengerKeys();
        
        // Yeni ProductKey'i al ve aynı sayfada kal
        let newProductKey: string | null = null;
        
        // alternativeRoomGroup varsa (minNight durumu) - key roomGroup.key içinde
        if (response.alternativeRoomGroup && response.alternativeRoomGroup.length > 0) {
          newProductKey = response.alternativeRoomGroup[0]?.roomGroup?.key;
        }
        // Normal items varsa - KEY, productKey değil!
        else if (response.items && response.items.length > 0) {
          newProductKey = response.items[0]?.key;  // ← productKey değil, key!
        }
        
        if (newProductKey) {
          // Aynı sayfada kal, sadece ProductKey'i güncelle
          const encodedKey = encodeURIComponent(newProductKey);
          this.router.navigate(['/hotel-detail', encodedKey]).then(() => {
            this.isSearching = false;
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' as any }), 0);
          });
        } else {
          this.isSearching = false;
        }
      },
      error: (error) => {
        this.isSearching = false;
      }
    });
  }

  /**
   * Belirli bir oda için loading durumunu kontrol eder
   * @param roomKey Oda key'i
   * @returns boolean
   */
  isRoomBookingLoading(roomKey: string): boolean {
    return this.bookingValidationState[roomKey]?.loading || false;
  }

  /**
   * Belirli bir oda için hata mesajını getirir
   * @param roomKey Oda key'i
   * @returns string | null
   */
  getRoomBookingError(roomKey: string): string | null {
    return this.bookingValidationState[roomKey]?.error || null;
  }

  /**
   * Oda için hata mesajını temizler
   * @param roomKey Oda key'i
   */
  clearRoomBookingError(roomKey: string): void {
    if (this.bookingValidationState[roomKey]) {
      this.bookingValidationState[roomKey].error = null;
    }
  }

  /**
   * ⭐ YENİ: Tek yolcu için AddOn ekle (manageAddOn kullanarak)
   * @param addOnKey Addon key'i
   * @param passengerIndex Yolcu index'i
   */
  addAddon(addOnKey: string, passengerIndex: number): void {
    const passengerKey = this.passengerKeys[passengerIndex];
    
    if (!passengerKey) {
      this.errorMessage = 'Bir sorun oluştu. Lütfen sayfayı yenileyip tekrar deneyin.';
      return;
    }
    
    // ⭐ TraceId kontrolü (GetDetail'den gelen)
    if (!this.traceId) {
      this.errorMessage = 'Oturumunuzun süresi dolmuş. Lütfen sayfayı yenileyip tekrar deneyin.';
      return;
    }
    
    
    this.isAddonProcessing[addOnKey] = true;
    this.errorMessage = null;
    
    this.hotelService.manageAddOn({
      productKey: this.productKey,
      traceId: this.traceId, // ⭐ GetDetail'den gelen TraceId
      operations: [{
        addOnKey: addOnKey,
        passengerKey: passengerKey,
        type: 'Add'
      }]
    }).pipe(
      finalize(() => {
        this.isAddonProcessing[addOnKey] = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (response) => {
        // ⭐ YENİ ProductKey'i MUTLAKA güncelle
        if (response.productKey) {
          const oldKey = this.productKey;
          this.productKey = response.productKey;
        }
        
        // Fiyatı güncelle
        if (response.totalPrice) {
          const price = typeof response.totalPrice === 'number' ? response.totalPrice : response.totalPrice.value;
          this.currentTotalPrice = price;
        }
        
        // UI state güncelle
        this.selectedAddons[addOnKey] = true;
      },
      error: (error) => {
        this.errorMessage = 'Ek hizmet eklenirken bir sorun oluştu. Lütfen tekrar deneyin.';
      }
    });
  }

  /**
   * ⭐ YENİ: Aynı AddOn'u TÜM yolculara birden ekle
   * @param addOnKey Addon key'i
   */
  addAddonToAllPassengers(addOnKey: string): void {
    if (!this.passengerKeys || this.passengerKeys.length === 0) {
      this.errorMessage = 'Bir sorun oluştu. Lütfen sayfayı yenileyip tekrar deneyin.';
      return;
    }
    
    // ⭐ TraceId kontrolü (GetDetail'den gelen)
    if (!this.traceId) {
      this.errorMessage = 'Oturumunuzun süresi dolmuş. Lütfen sayfayı yenileyip tekrar deneyin.';
      return;
    }
    
    // ✅ TÜM yolcular için operations oluştur
    const operations = this.passengerKeys.map(passengerKey => ({
      addOnKey: addOnKey,
      passengerKey: passengerKey,
      type: 'Add' as const
    }));
    
    
    this.isAddonProcessing[addOnKey] = true;
    this.errorMessage = null;
    
    this.hotelService.manageAddOn({
      productKey: this.productKey,
      traceId: this.traceId, // ⭐ GetDetail'den gelen TraceId
      operations: operations  // ⭐ 3+ operation birden
    }).pipe(
      finalize(() => {
        this.isAddonProcessing[addOnKey] = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (response) => {
        // ⭐ YENİ ProductKey'i MUTLAKA güncelle
        if (response.productKey) {
          const oldKey = this.productKey;
          this.productKey = response.productKey;
        }
        
        // Fiyatı güncelle
        if (response.totalPrice) {
          const newPrice = typeof response.totalPrice === 'number' ? response.totalPrice : response.totalPrice.value;
          const priceIncrease = newPrice - this.currentTotalPrice;
          this.currentTotalPrice = newPrice;
          
        }
        
        // UI state güncelle
        this.selectedAddons[addOnKey] = true;
      },
      error: (error) => {
        this.errorMessage = 'Ek hizmet eklenirken bir sorun oluştu. Lütfen tekrar deneyin.';
      }
    });
  }

  /**
   * ⭐ YENİ: Tek yolcu için AddOn çıkar (manageAddOn kullanarak)
   * @param addOnKey Addon key'i
   * @param passengerIndex Yolcu index'i
   */
  removeAddon(addOnKey: string, passengerIndex: number): void {
    const passengerKey = this.passengerKeys[passengerIndex];
    
    if (!passengerKey) {
      this.errorMessage = 'Bir sorun oluştu. Lütfen sayfayı yenileyip tekrar deneyin.';
      return;
    }
    
    // ⭐ TraceId kontrolü (GetDetail'den gelen)
    if (!this.traceId) {
      this.errorMessage = 'Oturumunuzun süresi dolmuş. Lütfen sayfayı yenileyip tekrar deneyin.';
      return;
    }
    
    
    this.isAddonProcessing[addOnKey] = true;
    this.errorMessage = null;
    
    this.hotelService.manageAddOn({
      productKey: this.productKey,
      traceId: this.traceId, // ⭐ GetDetail'den gelen TraceId
      operations: [{
        addOnKey: addOnKey,
        passengerKey: passengerKey,
        type: 'Remove'
      }]
    }).pipe(
      finalize(() => {
        this.isAddonProcessing[addOnKey] = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (response) => {
        // ⭐ YENİ ProductKey'i MUTLAKA güncelle
        if (response.productKey) {
          const oldKey = this.productKey;
          this.productKey = response.productKey;
        }
        
        // Fiyatı güncelle
        if (response.totalPrice) {
          const price = typeof response.totalPrice === 'number' ? response.totalPrice : response.totalPrice.value;
          this.currentTotalPrice = price;
        }
        
        // UI state güncelle
        this.selectedAddons[addOnKey] = false;
      },
      error: (error) => {
        this.errorMessage = 'Ek hizmet kaldırılırken bir sorun oluştu. Lütfen tekrar deneyin.';
      }
    });
  }
}